import type { ProbationDecisionPayload } from '@gokrew/models/company-dashboard';
import type {
  BatchInvitePayload,
  GetEmployeeByIdResponse,
  GetEmployeeListParams,
  GetEmployeeListResponse,
  EmployeePersonalInformation,
  GetEmployeeEducationListResponse,
  EmployeeWorkInformation,
  GetEmployeeFamilyListResponse,
  EmployeeFamily,
  EmployeeEducation,
  AddEmployee,
  EmployeeAddress,
  GetEmployeeAddress,
  GetCompanyDocumentType,
  CompanyDocumentType,
  EmployeeDocument,
  GetEmployeeDocuments,
  GetEmployeeSalaryListResponse,
  EmployeeSalary,
  EmployeeImportJobsParams,
  EmployeeImportJobDataParams,
  GetEligiblePoliciesParams,
  EligiblePoliciesAfterUpdateApiResponse,
  UpdateWalletAndGrantPayload,
  UpdateWalletAndGrantResponse,
  grantInitialLeavesPayload,
  CreateEmployeeEducationResponse,
  CreateTerminationPayload,
  GetEmployeeTerminationsResponse,
  UpdateTerminationPayload,
} from '@/types/employee';
import api from './client';
import qs from 'qs';
import type { ApiResponse } from '@gokrew/models/generic';
import type {
  AssignManagersPayload,
  BaseBankAccountParams,
  BulkImportTemplateValues,
  CreateEmployeeBankDetailsAPIPayload,
  DeleteEmployeeBankDetailsAPIPayload,
  EmployeeBankDetails,
  EmployeeBankDetailsApi,
  EmployeeEducationFormValues,
  GetBankAccountFieldsPayload,
  ResetPasswordResponse,
  UpdateEmployeeBankDetailsAPIPayload,
} from '@gokrew/models/employee';
import type {
  AdjustSalaryPayload,
  BenefitEnrollment,
  Bonus,
  CompensationSummary,
  BonusType,
  CreateBonusPayload,
  EnrollBenefitPayload,
  GetBenefitEnrollmentsParams,
  GetBenefitEnrollmentsResponse,
  GetBonusTypesParams,
  GetBonusTypesResponse,
  GetEmployeeBonusesParams,
  GetEmployeeBonusesResponse,
} from '@gokrew/models/Compensation';
import type { OptionalPageLimit } from '@gokrew/models/common';
export async function getEmployeeList(
  companyId: string,
  params: GetEmployeeListParams
) {
  const { data } = await api.get<GetEmployeeListResponse>(
    `/companies/${companyId}/employees`,
    {
      params,
      paramsSerializer: (params) => qs.stringify(params),
    }
  );
  return data;
}

export async function getEmployeeById(params: {
  companyId: string;
  employeeId: string;
}) {
  const { data } = await api.get<GetEmployeeByIdResponse>(
    `/companies/${params.companyId}/employees/${params.employeeId}`,
    {
      params,
    }
  );
  return data;
}

export async function createEmployee(
  companyId: string,
  data: Partial<AddEmployee>
) {
  const res = await api.post(`/companies/${companyId}/employees`, data);
  return res.data;
}

export async function updateEmployeePersonalInformation(params: {
  companyId: string;
  employeeId: string;
  data: Partial<EmployeePersonalInformation>;
}) {
  const { companyId, employeeId, data } = params;
  const response = await api.put<GetEmployeeByIdResponse>(
    `/companies/${companyId}/employees/${employeeId}/personal-information`,
    data
  );

  return response.data;
}

export async function updateEmployeeWorkInformation(params: {
  companyId: string;
  employeeId: string;
  data: Partial<EmployeeWorkInformation>;
}) {
  const { companyId, employeeId, data } = params;

  const response = await api.put<GetEmployeeByIdResponse>(
    `/companies/${companyId}/employees/${employeeId}/work-information`,
    data
  );

  return response.data;
}

/** Move an employee to another branch (location). */
export async function transferEmployeeLocation(params: {
  companyId: string;
  employeeId: string;
  data: {
    branchId: string;
    keepDepartmentAndDesignation: boolean;
    departmentId?: string;
    designationId?: string;
  };
}) {
  const { companyId, employeeId, data } = params;

  const response = await api.put<GetEmployeeByIdResponse>(
    `/companies/${companyId}/employees/${employeeId}/transfer-location`,
    data
  );

  return response.data;
}

/** Confirm a probation, or extend it with a reason (dashboard feed). */
export async function decideEmployeeProbation(params: {
  companyId: string;
  employeeId: string;
  data: ProbationDecisionPayload;
}) {
  const { companyId, employeeId, data } = params;
  const response = await api.post(
    `/companies/${companyId}/employees/${employeeId}/probation-decision`,
    data
  );
  return response.data;
}

export async function getEmployeeEducations(
  companyId: string,
  employeeId: string
): Promise<GetEmployeeEducationListResponse> {
  const { data } = await api.get(
    `/companies/${companyId}/employees/${employeeId}/education`
  );
  return data;
}

export async function getEmployeeFamily(
  companyId: string,
  employeeId: string
): Promise<GetEmployeeFamilyListResponse> {
  const { data } = await api.get(
    `/companies/${companyId}/employees/${employeeId}/family`
  );
  return data;
}

/**
 * Create Employee Family
 *
 */
export async function createEmployeeFamiy(
  companyId: string,
  employeeId: string,
  data: Partial<EmployeeFamily>
) {
  const res = await api.post<EmployeeFamily>(
    `/companies/${companyId}/employees/${employeeId}/family`,
    data
  );
  return res.data;
}

/**
 * Update an existing employee family
 *
 */
export async function updateEmployeeFamily(
  companyId: string,
  employeeId: string,
  id: string,
  data: Partial<EmployeeFamily>
) {
  const res = await api.put<EmployeeFamily>(
    `/companies/${companyId}/employees/${employeeId}/family/${id}`,
    data
  );
  return res.data;
}

export async function deleteEmployeeFamily(
  companyId: string,
  employeeId: string,
  id: string
) {
  const res = await api.delete(
    `/companies/${companyId}/employees/${employeeId}/family/${id}`
  );
  return res.data;
}

/**
 * Create Employee Education
 *
 */
export async function createEmployeeEducation(
  companyId: string,
  employeeId: string,
  data: EmployeeEducationFormValues
) {
  const res = await api.post<CreateEmployeeEducationResponse>(
    `/companies/${companyId}/employees/${employeeId}/education`,
    data
  );

  return res.data; // { message, data }
}

/**
 * Update an existing employee education
 *
 */
export async function updateEmployeeEducation(
  companyId: string,
  employeeId: string,
  id: string,
  data: EmployeeEducationFormValues
) {
  const res = await api.put<EmployeeEducation>(
    `/companies/${companyId}/employees/${employeeId}/education/${id}`,
    data
  );
  return res.data;
}

export async function deleteEmployeeEducation(
  companyId: string,
  employeeId: string,
  id: string
) {
  const res = await api.delete(
    `/companies/${companyId}/employees/${employeeId}/education/${id}`
  );
  return res.data;
}

export async function createEmployeeTermination(
  companyId: string,
  employeeId: string,
  payload: CreateTerminationPayload
) {
  const { data } = await api.post(
    `/companies/${companyId}/employees/${employeeId}/terminations`,
    payload
  );

  return data;
}

export async function getEmployeeTerminations(
  companyId: string,
  employeeId: string,
  params?: {
    status?: string;
    limit?: number;
  }
): Promise<GetEmployeeTerminationsResponse> {
  const { data } = await api.get(
    `/companies/${companyId}/employees/${employeeId}/terminations`,
    {
      params,
    }
  );
  return data;
}

export async function executeEmployeeTermination(
  companyId: string,
  employeeId: string,
  terminationId: string
) {
  const { data } = await api.post(
    `/companies/${companyId}/employees/${employeeId}/terminations/${terminationId}/execute`
  );
  return data;
}

export async function cancelEmployeeTermination(
  companyId: string,
  employeeId: string,
  terminationId: string
) {
  const { data } = await api.delete(
    `/companies/${companyId}/employees/${employeeId}/terminations/${terminationId}`
  );

  return data;
}

export async function updateEmployeeTermination(
  companyId: string,
  employeeId: string,
  terminationId: string,
  payload: UpdateTerminationPayload
) {
  const { data } = await api.put(
    `/companies/${companyId}/employees/${employeeId}/terminations/${terminationId}`,
    payload
  );

  return data;
}

/**
 * Reactivates an employee. With leave years on, `returning` restarts their
 * service on `rehireDate` (the hire date becomes it); without it, or false,
 * it is a reinstatement and the hire date stays.
 */
export async function activateEmployee(
  companyId: string,
  id: string,
  body?: { returning?: boolean; rehireDate?: string }
) {
  const res = await api.put<EmployeeEducation>(
    `/companies/${companyId}/employees/${id}/activate`,
    body ?? {}
  );
  return res.data;
}

export async function addEmployeeAddress(
  companyId: string,
  employeeId: string | undefined,
  data: Partial<EmployeeAddress>
) {
  const res = await api.post<EmployeeAddress>(
    `/companies/${companyId}/employees/${employeeId}/address`,
    data
  );
  return res.data;
}

export async function updateEmployeeAddress(
  companyId: string,
  employeeId: string | undefined,
  id: string | undefined,
  data: Partial<EmployeeAddress>
) {
  const res = await api.put<EmployeeAddress>(
    `/companies/${companyId}/employees/${employeeId}/address/${id}`,
    data
  );
  return res.data;
}

export async function getEmployeeAddress(
  companyId: string,
  employeeId: string
): Promise<GetEmployeeAddress> {
  const { data } = await api.get(
    `/companies/${companyId}/employees/${employeeId}/address`
  );
  return data;
}

export async function createCompanyDocumentType(
  companyId: string,
  data: { name: string; description: string }
) {
  const res = await api.post<{ message: string; data: CompanyDocumentType }>(
    `/companies/${companyId}/document-types`,
    data
  );
  return res.data;
}

export async function getCompanyDocumentTypes(
  companyId: string
): Promise<GetCompanyDocumentType> {
  const { data } = await api.get(`/companies/${companyId}/document-types`);
  return data;
}

export async function updateCompanyDocumentType(
  companyId: string,
  documentTypeId: string,
  data: { name: string; description: string }
) {
  const res = await api.put<GetCompanyDocumentType>(
    `/companies/${companyId}/document-types/${documentTypeId}`,
    data
  );
  return res.data;
}

export async function deleteCompanyDocumentType(
  companyId: string,
  documentTypeId: string
) {
  const res = await api.delete(
    `/companies/${companyId}/document-types/${documentTypeId}`
  );
  return res.data;
}

export async function createEmployeeDocument(
  companyId: string,
  employeeId: string,
  data: {
    documentTypeId: string;
    documentUrl: string;
    expiresOn?: string;
  }
) {
  const res = await api.post<{ message: string; data: EmployeeDocument[] }>(
    `/companies/${companyId}/employees/${employeeId}/documents`,
    data
  );
  return res.data;
}

/**
 * Sets or clears (`null`) a document's expiry date in place, without a new
 * upload. The rest of the document is left as it is.
 */
export async function updateEmployeeDocumentExpiry(
  companyId: string,
  employeeId: string,
  id: string,
  expiresOn: string | null
) {
  const res = await api.put<{ message: string; data: EmployeeDocument }>(
    `/companies/${companyId}/employees/${employeeId}/documents/${id}`,
    { expiresOn }
  );
  return res.data;
}

export async function getEmployeeDocumentById(params: {
  companyId: string;
  employeeId: string;
  id: string;
}) {
  const { data } = await api.get<{ message: string; data: EmployeeDocument[] }>(
    `/companies/${params.companyId}/employees/${params.employeeId}/documents/${params.id}`,
    {
      params,
    }
  );

  return data;
}

export async function getEmployeeDocuments(
  companyId: string,
  employeeId: string
): Promise<GetEmployeeDocuments> {
  const { data } = await api.get(
    `/companies/${companyId}/employees/${employeeId}/documents`
  );
  return data;
}

export async function deleteEmployeeDocument(
  companyId: string,
  employeeId: string,
  id: string
) {
  const res = await api.delete(
    `/companies/${companyId}/employees/${employeeId}/documents/${id}`
  );
  return res.data;
}

export async function getEmployeeSalaries(
  companyId: string,
  employeeId: string,
  params?: OptionalPageLimit
): Promise<GetEmployeeSalaryListResponse> {
  const { data } = await api.get(
    `/companies/${companyId}/employees/${employeeId}/salaries`,
    {
      params,
    }
  );

  return data;
}

export async function createEmployeeSalary(
  companyId: string,
  employeeId: string,
  data: Partial<EmployeeSalary>
) {
  const response = await api.post(
    `/companies/${companyId}/employees/${employeeId}/salaries`,
    data
  );
  return response.data;
}

export async function updateEmployeeSalary(
  companyId: string,
  employeeId: string,
  salaryId: string,
  data: Partial<EmployeeSalary>
) {
  const response = await api.put<EmployeeSalary>(
    `/companies/${companyId}/employees/${employeeId}/salaries/${salaryId}`,
    data
  );
  return response.data;
}

/** Hours worked per week of one salary; null follows the schedule. */
export async function setEmployeeSalaryWeeklyHours(
  companyId: string,
  employeeId: string,
  salaryId: string,
  weeklyHours: number | null
) {
  const response = await api.patch<EmployeeSalary>(
    `/companies/${companyId}/employees/${employeeId}/salaries/${salaryId}/weekly-hours`,
    { weeklyHours }
  );
  return response.data;
}

export async function deleteEmployeeSalary(
  companyId: string,
  employeeId: string,
  salaryId: string
) {
  const { data } = await api.delete(
    `/companies/${companyId}/employees/${employeeId}/salaries/${salaryId}`
  );
  return data;
}

/** Adjust compensation: the new pay from a date, ending the current one. */
export async function adjustEmployeeSalary(
  companyId: string,
  employeeId: string,
  payload: AdjustSalaryPayload
) {
  const { data } = await api.post(
    `/companies/${companyId}/employees/${employeeId}/salaries/adjust`,
    payload
  );
  return data;
}

/** Base pay timeline + total compensation per year, for the charts. */
export async function getCompensationSummary(
  companyId: string,
  employeeId: string
): Promise<ApiResponse<CompensationSummary>> {
  const { data } = await api.get(
    `/companies/${companyId}/employees/${employeeId}/salaries/summary`
  );
  return data;
}

// ── Bonus Types ───────────────────────────────────────────────────────────────

export async function getBonusTypes(
  companyId: string,
  params?: GetBonusTypesParams
): Promise<GetBonusTypesResponse> {
  const { data } = await api.get<GetBonusTypesResponse>(
    `/companies/${companyId}/bonus-types`,
    { params }
  );
  return data;
}

export async function createBonusType(
  companyId: string,
  name: string
): Promise<{ data: BonusType }> {
  const { data } = await api.post(`/companies/${companyId}/bonus-types`, {
    name,
  });
  return data;
}

export async function updateBonusType(
  companyId: string,
  id: string,
  name: string
): Promise<{ data: BonusType }> {
  const { data } = await api.put(`/companies/${companyId}/bonus-types/${id}`, {
    name,
  });
  return data;
}
export async function deleteBonusType(
  companyId: string,
  id: string
): Promise<void> {
  await api.delete(`/companies/${companyId}/bonus-types/${id}`);
}

// ── Employee Bonuses ──────────────────────────────────────────────────────────

export async function getEmployeeBonuses(
  companyId: string,
  employeeId: string,
  params?: GetEmployeeBonusesParams
): Promise<GetEmployeeBonusesResponse> {
  const { data } = await api.get<GetEmployeeBonusesResponse>(
    `/companies/${companyId}/employees/${employeeId}/bonuses`,
    { params }
  );
  return data;
}

export async function createEmployeeBonus(
  companyId: string,
  employeeId: string,
  payload: CreateBonusPayload
): Promise<{ data: Bonus }> {
  const { data } = await api.post(
    `/companies/${companyId}/employees/${employeeId}/bonuses`,
    payload
  );
  return data;
}

export async function updateEmployeeBonus(
  companyId: string,
  employeeId: string,
  bonusId: string,
  payload: CreateBonusPayload
): Promise<{ data: Bonus }> {
  const { data } = await api.put(
    `/companies/${companyId}/employees/${employeeId}/bonuses/${bonusId}`,
    payload
  );
  return data;
}

export async function deleteEmployeeBonus(
  companyId: string,
  employeeId: string,
  bonusId: string
): Promise<void> {
  await api.delete(
    `/companies/${companyId}/employees/${employeeId}/bonuses/${bonusId}`
  );
}

// ── Employee Benefits ─────────────────────────────────────────────────────────

export async function getEmployeeBenefitEnrollments(
  companyId: string,
  employeeId: string,
  params?: GetBenefitEnrollmentsParams
): Promise<GetBenefitEnrollmentsResponse> {
  const { data } = await api.get<GetBenefitEnrollmentsResponse>(
    `/companies/${companyId}/employees/${employeeId}/benefits`,
    { params }
  );
  return data;
}

export async function createEmployeeBenefitEnrollment(
  companyId: string,
  employeeId: string,
  payload: EnrollBenefitPayload
): Promise<{ data: BenefitEnrollment }> {
  const { data } = await api.post(
    `/companies/${companyId}/employees/${employeeId}/benefits`,
    payload
  );
  return data;
}

export async function updateEmployeeBenefitEnrollment(
  companyId: string,
  employeeId: string,
  id: string,
  payload: EnrollBenefitPayload
): Promise<{ data: BenefitEnrollment }> {
  const { data } = await api.put(
    `/companies/${companyId}/employees/${employeeId}/benefits/${id}`,
    payload
  );
  return data;
}

export async function deleteEmployeeBenefitEnrollment(
  companyId: string,
  employeeId: string,
  id: string
): Promise<void> {
  await api.delete(
    `/companies/${companyId}/employees/${employeeId}/benefits/${id}`
  );
}
/**
 * Upload jobs CSV for a company
 */
export async function uploadJobsCsv(companyId: string, file: File) {
  const formData = new FormData();
  formData.append('file', file);

  const res = await api.post(`/companies/${companyId}/jobs`, formData, {
    headers: {
      'Content-Type': undefined,
    },
  });

  return res.data;
}

/**
 * Download jobs CSV template for a company
 */
export async function downloadJobsCsvTemplate(companyId: string) {
  const response = await api.get<Blob>(
    `/companies/${companyId}/jobs/template`,
    {
      responseType: 'blob', // important to treat as file
    }
  );

  return response.data;
}

/**
 * Get all CSV jobs for a company
 */

export async function getImportJobs(params: EmployeeImportJobsParams) {
  const { companyId, page = 1, limit = 100 } = params;

  const res = await api.get(`/companies/${companyId}/jobs`, {
    params: { page, limit },
  });

  return res.data;
}

/**
 * Get import jobs data for an employee by id
 */
export async function getImportJobData(params: EmployeeImportJobDataParams) {
  const { companyId, jobId, page = 1, limit = 10, status } = params;
  const res = await api.get(`/companies/${companyId}/jobs/${jobId}/data`, {
    params: { page, limit, ...(status ? { status } : {}) },
  });
  return res.data;
}

/**
 * Get import jobs stats for an bulk job
 */
export async function getImportJobStats(companyId: string, jobId: string) {
  const res = await api.get(`/companies/${companyId}/jobs/${jobId}/stats`);
  return res.data.data;
}

/** Branch, department and designation names the import accepts. */
export async function getImportTemplateValues(
  companyId: string
): Promise<BulkImportTemplateValues> {
  const res = await api.get<ApiResponse<BulkImportTemplateValues>>(
    `/companies/${companyId}/jobs/template/values`
  );
  return res.data.data;
}

export async function sendInvitation(companyId: string, id: string) {
  const res = await api.put(
    `/companies/${companyId}/employees/${id}/sendInvitation`
  );
  return res.data;
}

/** Who, in a selection, has no email and will not be notified (D163). */
export async function getUnreachableRecipients(
  companyId: string,
  selection: { employeeIds?: string[]; scope?: 'all' }
) {
  const res = await api.post<{
    data: { count: number; employees: { id: string; name: string }[] };
  }>(`/companies/${companyId}/employees/unreachable`, selection);
  return res.data.data;
}

/** What `PUT employees/:id/email` answers. */
export interface SetEmployeeEmailResponse {
  data: {
    email: string;
    /** Set when a new invitation with a temporary password went out. */
    rawPassword?: string;
    portalUrl?: string;
    /** Changing an existing email only: the old address it replaced. */
    previousEmail?: string;
    changed?: boolean;
    /** Changing only: the person was emailed (they were already invited). */
    notified?: boolean;
    invitationSent?: boolean;
  };
}

/**
 * Sets an employee's login email: gives one to an employee who had none
 * (spec 2026-10-15, D160), or changes a wrong one (2026-10-29), in which
 * case the server decides who is emailed and `sendInvitation` is ignored.
 * The answer has the invitation shape: a temporary password when one was
 * sent.
 */
export async function setEmployeeEmail(
  companyId: string,
  id: string,
  data: { email: string; sendInvitation?: boolean }
) {
  const res = await api.put<SetEmployeeEmailResponse>(
    `/companies/${companyId}/employees/${id}/email`,
    data
  );
  return res.data;
}

export async function resetEmployeePassword(companyId: string, id: string) {
  const res = await api.put<ResetPasswordResponse>(
    `/companies/${companyId}/employees/${id}/reset-password`
  );
  return res.data;
}

export async function assignEmployeeManagers(
  companyId: string,
  data: AssignManagersPayload
) {
  const res = await api.post(`/companies/${companyId}/leave-manager`, data);
  return res.data;
}

// Get Bank Details Fields
export const getBankAccountFields = async (
  params: GetBankAccountFieldsPayload
) => {
  const { companyId, employeeId, code } = params;
  const { data } = await api.get(
    `/companies/${companyId}/employees/${employeeId}/bank-accounts/${code}/fields`
  );
  return data;
};

export const createEmployeeBankDetails = async (
  params: CreateEmployeeBankDetailsAPIPayload
) => {
  const { companyId, employeeId, payload } = params;
  const { data } = await api.post(
    `/companies/${companyId}/employees/${employeeId}/bank-accounts`,
    payload
  );

  return data;
};

export const updateEmployeeBankDetails = async (
  params: UpdateEmployeeBankDetailsAPIPayload
) => {
  const { companyId, employeeId, id, payload } = params;
  const { data } = await api.put(
    `/companies/${companyId}/employees/${employeeId}/bank-accounts/${id}`,
    payload
  );

  return data;
};

export const getEmployeeBankDetails = async (
  params: BaseBankAccountParams
): Promise<EmployeeBankDetails | null> => {
  const { companyId, employeeId } = params;
  const { data } = await api.get<{
    message: string;
    data: EmployeeBankDetailsApi | null;
  }>(`/companies/${companyId}/employees/${employeeId}/bank-accounts`);

  if (!data?.data) return null;

  const countryFields: Record<string, string> = {};
  data.data.fieldValues?.forEach((fv) => {
    if (fv?.countryBankingField?.fieldKey) {
      countryFields[fv.countryBankingField.fieldKey] = fv.fieldValue;
    }
  });

  return {
    ...data.data,
    ...(Object.keys(countryFields).length ? { countryFields } : {}),
  };
};

// -----------------------------------------------------------------------------
// Delete an employee's bank account
// -----------------------------------------------------------------------------
export const deleteEmployeeBankDetails = async (
  params: DeleteEmployeeBankDetailsAPIPayload
) => {
  const { companyId, employeeId, bankAccountId } = params;
  const { data } = await api.delete(
    `/companies/${companyId}/employees/${employeeId}/bank-accounts/${bankAccountId}`
  );

  return data;
};

export const batchInviteEmployees = (
  companyId: string,
  payload: BatchInvitePayload,
  branchId?: string
) => {
  return api.put(`/companies/${companyId}/employees/batch-invite`, payload, {
    params: branchId ? { branchId } : undefined,
  });
};

export async function getEligibleLeavePolicies(
  companyId: string,
  params: GetEligiblePoliciesParams
) {
  const { data } = await api.get(
    `/companies/${companyId}/leave-policies/eligible-policies`,
    { params }
  );

  return data;
}

export async function grantInitialLeaves(
  companyId: string,
  payload: grantInitialLeavesPayload
) {
  const { data } = await api.post(
    `/companies/${companyId}/leave-policies/grant-initial-leaves`,
    payload
  );

  return data;
}

export async function getEligibleLeavePoliciesAfterUpdate(
  companyId: string,
  employeeId: string,
  params: GetEligiblePoliciesParams
): Promise<EligiblePoliciesAfterUpdateApiResponse> {
  const { data } = await api.get(
    `/companies/${companyId}/leave-policies/eligible-policies/${employeeId}`,
    { params }
  );

  return data;
}

export async function updateWalletAndGrantInitialLeaves(
  companyId: string,
  employeeId: string,
  data: UpdateWalletAndGrantPayload
) {
  const response = await api.post<UpdateWalletAndGrantResponse>(
    `/companies/${companyId}/leave-policies/update-wallet-and-grant-initial-leaves`,
    {
      employeeId,
      ...data,
    }
  );

  return response.data;
}
